import { SOURCE_CHART_HEX } from '../../theme/sourceColors';
import { LIGHT_HEX } from '../../theme/tokens';

export type DataSource = 'peach' | 'rowhero' | 'concept2';

export const DATA_SOURCE_CONFIG: Record<
  DataSource,
  {
    label: string;
    color: string;
  }
> = {
  peach: {
    label: 'Peach (On-Water)',
    color: SOURCE_CHART_HEX.peach,
  },
  rowhero: {
    label: 'RowHero (Indoor)',
    color: LIGHT_HEX.rowing.sky,
  },
  concept2: {
    label: 'Concept2 (Indoor)',
    color: SOURCE_CHART_HEX.concept2,
  },
};

export interface Session {
  id: string;
  date: string;
  dataSource: DataSource;
  sessionType: string;
  avgPower: number;
  maxPower: number;
  startTime: string;
  endTime: string;
}

export interface EventMarker {
  id: string;
  startDate: string;
  endDate?: string;
  label: string;
  category: string;
}

export interface TimelinePoint {
  x: Date;
  stackIndex: number;
  session: Session;
}

export interface TimelineEventPoint {
  start: Date;
  end: Date;
  event: EventMarker;
}

export const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const buildTimelineData = (
  sessions: Session[],
  events: EventMarker[]
): {
  points: TimelinePoint[];
  eventPoints: TimelineEventPoint[];
  maxStack: number;
} => {
  const counts: Record<string, number> = {};
  let maxStack = 0;

  const points = sessions
    .map((session) => {
      const stackGroupKey = `${session.dataSource}-${session.date}`;
      const nextIndex = (counts[stackGroupKey] ?? 0) + 1;
      counts[stackGroupKey] = nextIndex;
      maxStack = Math.max(maxStack, nextIndex);

      return {
        x: new Date(session.date),
        stackIndex: nextIndex,
        session,
      };
    })
    .sort((a, b) => a.x.getTime() - b.x.getTime());

  const eventPoints = events
    .map((event) => {
      const start = new Date(event.startDate);
      const end = event.endDate ? new Date(event.endDate) : start;
      const safeEnd = end.getTime() >= start.getTime() ? end : start;

      return {
        start,
        end: safeEnd,
        event,
      };
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  return { points, eventPoints, maxStack };
};

export const computeTimelineDomain = (
  sessionPoints: TimelinePoint[],
  eventPoints: TimelineEventPoint[],
  fallbackDate: Date = new Date()
): [Date, Date] => {
  const allDates = [
    ...sessionPoints.map((point) => point.x),
    ...eventPoints.map((point) => point.start),
    ...eventPoints.map((point) => point.end),
  ].filter((date) => !Number.isNaN(date.getTime()));

  if (!allDates.length) {
    const fallbackStart = new Date(fallbackDate.getTime() - DAY_IN_MS);
    const fallbackEnd = new Date(fallbackDate.getTime() + DAY_IN_MS);
    return [fallbackStart, fallbackEnd];
  }

  allDates.sort((a, b) => a.getTime() - b.getTime());

  const minDate = allDates[0];
  const maxDate = allDates[allDates.length - 1];

  if (minDate.getTime() === maxDate.getTime()) {
    return [new Date(minDate.getTime() - DAY_IN_MS), new Date(maxDate.getTime() + DAY_IN_MS)];
  }

  return [minDate, maxDate];
};
